import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
  IconButton,
  Paper,
} from '@mui/material';
import { X, Info, RotateCcw } from 'lucide-mui';

interface ImportFailedModalProps {
  open: boolean;
  onClose: () => void;
  invalidUrl?: string | null;
  onTryAgain?: () => void;
}

const ImportFailedModal: React.FC<ImportFailedModalProps> = ({
  open,
  onClose,
  invalidUrl,
  onTryAgain,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: theme.shadows[8],
          margin: theme.spacing(2),
          position: 'relative',
        },
      }}
    >
      {/* Close button */}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: theme.palette.text.secondary,
          zIndex: 1,
          '&:hover': {
            color: theme.palette.text.primary,
          },
        }}
      >
        <X sx={{ width: 20, height: 20 }} />
      </IconButton>

      <DialogContent sx={{ pt: 4, px: 3, pb: 2 }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            mb: 2,
            color: theme.palette.text.primary,
          }}
        >
          Import Failed
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.primary,
            mb: 2,
            lineHeight: 1.5,
          }}
        >
          Unable to import. Check that your URL is valid and the content is
          accessible.
        </Typography>

        {invalidUrl && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mb: 3,
              fontStyle: 'italic',
              wordBreak: 'break-all',
            }}
          >
            URL: {invalidUrl}
          </Typography>
        )}

        {/* Info box */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            p: 2,
            mb: 3,
            backgroundColor: `${theme.palette.info.main}15`,
            borderRadius: 2,
            gap: 1.5,
          }}
        >
          <Info
            sx={{
              width: 20,
              height: 20,
              color: theme.palette.info.main,
              mt: 0.25,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.4,
            }}
          >
            Possible issues: incorrect format, private content, or restricted
            access.
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          gap: 1.5,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: '24px',
            color: theme.palette.text.primary,
            borderColor: theme.palette.divider,
            '&:hover': {
              borderColor: theme.palette.text.secondary,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          Cancel
        </Button>

        {onTryAgain && (
          <Button
            onClick={onTryAgain}
            variant="contained"
            startIcon={<RotateCcw sx={{ width: 16, height: 16 }} />}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              borderRadius: '24px',
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Try Again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportFailedModal;
